import type { ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { FadeLink } from '@/components/generals/fade-link'
import { Wordmark } from '@/components/generals/wordmark'
import { RedLightToggle, SoundToggle } from '@/components/generals/toggles'
import { BookMenu } from '@/components/generals/book-menu'
import { MobileMenu } from '@/components/generals/mobile-menu'
import { primaryNav } from '@/constants/site'

export const Header = (): ReactNode => {
    const router = useRouter()
    const [hidden, setHidden] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const menuButtonRef = useRef<HTMLButtonElement | null>(null)

    useEffect(() => {
        let lastY = window.scrollY
        let ticking = false

        const update = () => {
            const y = window.scrollY
            const delta = y - lastY

            if (y < 80) setHidden(false)
            else if (delta > 6) setHidden(true)
            else if (delta < -6) setHidden(false)

            setScrolled(y > 24)
            lastY = y
            ticking = false
        }

        const handleScroll = () => {
            if (ticking) return
            ticking = true
            requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', handleScroll, { passive: true })

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const closeMenu = useCallback(() => {
        setMenuOpen(false)
    }, [])

    const wasOpen = useRef(false)

    useEffect(() => {
        // Hand focus back to the button that opened the menu, so keyboard users are not dropped at the top of the page.
        if (!menuOpen && wasOpen.current) menuButtonRef.current?.focus({ preventScroll: true })
        wasOpen.current = menuOpen
    }, [menuOpen])

    const isActive = (href: string) => router.pathname === href || router.pathname.startsWith(`${href}/`)

    return (
        <>
            <a
                href='#main'
                className='label-mono fixed left-4 top-3 z-[90] -translate-y-24 bg-brass px-4 py-2 text-ink transition-transform focus:translate-y-0'
            >
                Skip to content
            </a>

            <header
                onFocusCapture={() => setHidden(false)}
                className={cn(
                    'site-header fixed inset-x-0 top-0 z-50 transition-transform duration-700 ease-[cubic-bezier(0.65,0,0.35,1)]',
                    hidden && !menuOpen ? '-translate-y-full' : 'translate-y-0'
                )}
            >
                <div
                    aria-hidden
                    className={cn(
                        'pointer-events-none absolute inset-0 bg-gradient-to-b from-ink/90 via-ink/50 to-transparent transition-opacity duration-700',
                        scrolled ? 'opacity-100' : 'opacity-0'
                    )}
                />

                <div className='container-page relative flex h-16 items-center justify-between gap-6'>
                    <FadeLink href='/' aria-label='CosmicLens Observatory, home' className='shrink-0'>
                        <Wordmark />
                    </FadeLink>

                    <nav aria-label='Primary' className='hidden xl:block'>
                        <ul className='flex items-center gap-6 2xl:gap-9'>
                            {primaryNav.map(link => (
                                <li key={link.href}>
                                    <FadeLink
                                        href={link.href}
                                        aria-current={isActive(link.href) ? 'page' : undefined}
                                        className={cn(
                                            'label-mono relative py-2 transition-colors duration-500',
                                            isActive(link.href) ? 'text-star' : 'text-star-2 hover:text-star'
                                        )}
                                    >
                                        {link.label}
                                        <span
                                            aria-hidden
                                            className={cn(
                                                'absolute -bottom-0.5 left-1/2 h-px -translate-x-1/2 bg-brass transition-all duration-500',
                                                isActive(link.href) ? 'w-4' : 'w-0'
                                            )}
                                        />
                                    </FadeLink>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-1 sm:gap-2'>
                        <SoundToggle className='hidden px-3 md:inline-flex xl:hidden 2xl:inline-flex' />
                        <RedLightToggle className='hidden sm:inline-flex' />
                        <RedLightToggle className='sm:hidden' showLabel={false} />
                        <BookMenu className='hidden sm:block' />
                        <button
                            ref={menuButtonRef}
                            type='button'
                            aria-expanded={menuOpen}
                            aria-controls='mobile-menu'
                            onClick={() => setMenuOpen(true)}
                            className='label-mono inline-flex h-9 items-center gap-2 rounded-full border border-hair-2 px-4 text-star xl:hidden'
                        >
                            <span className='flex flex-col gap-[3px]' aria-hidden>
                                <span className='block h-px w-3.5 bg-star' />
                                <span className='block h-px w-2.5 bg-star' />
                            </span>
                            Menu
                        </button>
                    </div>
                </div>
            </header>

            <MobileMenu open={menuOpen} onClose={closeMenu} />
        </>
    )
}
